/**
 * Entries View - Admin view to review attorney entries for a specific date.
 *
 * Combines Entries table and Calendar views in a single tabbed layout.
 * Top row: Attorney selector (left) + Entries/Calendar tab pills (right).
 *
 * Uses the same EntriesTable component as the attorney view (DRY principle).
 * Differences from attorney view:
 * - No checkboxes (read-only)
 * - Click opens detail dialog instead of edit dialog
 * - No export/group/delete actions
 */

import {
	type ColumnFiltersState,
	type ColumnSizingState,
	type ExpandedState,
	getCoreRowModel,
	getExpandedRowModel,
	getFilteredRowModel,
	getGroupedRowModel,
	getSortedRowModel,
	type OnChangeFn,
	type Row,
	type RowSelectionState,
	type SortingState,
	useReactTable,
	type VisibilityState,
} from "@tanstack/react-table";
import { useAuth } from "@workos-inc/authkit-react";
import { Check, ChevronsUpDown } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
// Nav arrow icons for month navigation
import navArrowLeftIcon from "@/assets/icons/navArrowLeft.svg";
import navArrowLeftWhiteIcon from "@/assets/icons/navArrowLeftWhite.svg";
import navArrowRightIcon from "@/assets/icons/navArrowRight.svg";
import navArrowRightWhiteIcon from "@/assets/icons/navArrowRightWhite.svg";
import {
	columns,
	DEFAULT_COLUMN_ORDER,
	DEFAULT_COLUMN_VISIBILITY,
	type EntriesViewMeta,
} from "@/features/attorney/views/Entries/columns";
import { EntriesTable } from "@/features/attorney/views/Entries/EntriesTable";
import type {
	Entry,
	EntryStatus,
} from "@/features/attorney/views/Entries/normalize";
import { Button } from "@/shared/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/shared/components/ui/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Tabs, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { useDarkMode } from "@/shared/hooks/useDarkMode";
import { cn } from "@/shared/lib/utils";
import { logger } from "@/shared/utils/logger";
import { useApi } from "@/shared/utils/request";
import { DatePicker } from "@/v2/components/date-picker/DatePicker";
import { FiltersPopover } from "../../attorney/components/HeaderBar/FiltersPopover";
import { useApplicationFilter } from "../../attorney/hooks/useApplicationFilter";
import calendarStyles from "../../attorney/views/CalendarView/CalendarView.module.css";
import AdminEntryDetailDialog, {
	type FeedbackPayload,
} from "../components/AdminEntryDetailDialog";
import {
	type AdminTransaction,
	useCalendarTransactions,
	useProfessionals,
	useRestoreAndExport,
	useUserTransactions,
} from "../hooks/useAdminData";
import { usePersistedState } from "../hooks/usePersistedState";
import styles from "./AdminViews.module.css";

function formatDateForApi(date: Date): string {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
}

/**
 * Derive entry status from source table and exported flag.
 * - deleted_transactions → "deleted"
 * - transactions + exported=true → "exported"
 * - transactions + exported=false → "tracked"
 */
function deriveStatus(tx: AdminTransaction): EntryStatus {
	if (tx.source === "deleted_transactions") return "deleted";
	if (tx.exported) return "exported";
	return "tracked";
}

/** Transform AdminTransaction to Entry type for EntriesTable */
function toEntry(tx: AdminTransaction): Entry {
	return {
		object: "entry",
		id: String(tx.id),
		status: deriveStatus(tx),
		date: tx.date.split("T")[0],
		startTime: tx.start_time,
		endTime: tx.end_time,
		units: tx.units,
		unitsSeconds: Math.round(tx.units * 3600),
		description: tx.description || "",
		application: tx.app_name || null,
		documentName: tx.document_name,
		billable: null,
		billingCategory: "unknown",
		billableCategory: "unknown",
		matter: {
			id: tx.matter_id,
			code: tx.matter_number,
			description: tx.matter_description,
			client: null,
		},
		codes: {
			task: tx.task_code,
			activity: tx.activity_code,
		},
	};
}

/** Get today's date as YYYY-MM-DD string */
function getTodayString(): string {
	return formatDateForApi(new Date());
}

export default function EntriesView() {
	const { darkMode } = useDarkMode();
	const { user } = useAuth();

	// Active tab: entries or calendar
	const [activeTab, setActiveTab] = usePersistedState<"entries" | "calendar">(
		"admin:entries:tab",
		"entries",
	);

	// Persisted filter state (survives navigation, refresh, browser close)
	const [selectedDateStr, setSelectedDateStr] = usePersistedState<string>(
		"admin:entries:date",
		getTodayString(),
	);
	const [selectedProfId, setSelectedProfId] = usePersistedState<string | null>(
		"admin:entries:professional",
		null,
	);

	// Derived Date object for calendar component
	const selectedDate = useMemo(() => {
		const [year, month, day] = selectedDateStr.split("-").map(Number);
		return new Date(year, month - 1, day);
	}, [selectedDateStr]);

	// Helper to update date from Date object
	const setSelectedDate = useCallback(
		(date: Date) => setSelectedDateStr(formatDateForApi(date)),
		[setSelectedDateStr],
	);

	// Application filter
	const {
		isVisible: isApplicationVisible,
		toggleApplication,
		filterEntries,
		showAll: showAllApplications,
		hideAll: hideAllApplications,
	} = useApplicationFilter(user?.email || "");

	// Calendar tab: month being displayed (independent of entries date picker)
	const [calendarMonthDate, setCalendarMonthDate] = useState<Date>(new Date());

	// Non-persisted UI state
	const [attorneySearch, setAttorneySearch] = useState("");
	const [attorneyOpen, setAttorneyOpen] = useState(false);

	// Table state for the shared Entries table.
	const [sorting, setSorting] = usePersistedState<SortingState>(
		"admin:entries:sorting",
		[{ id: "date", desc: true }],
	);
	const handleSortingChange = useCallback<OnChangeFn<SortingState>>(
		(updater) => {
			setSorting(typeof updater === "function" ? updater(sorting) : updater);
		},
		[setSorting, sorting],
	);
	const [expanded, setExpanded] = useState<ExpandedState>(true);
	const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
	const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
	const columnVisibility = useMemo<VisibilityState>(
		() => ({
			...DEFAULT_COLUMN_VISIBILITY,
			_select: false,
			billableCategory: false,
		}),
		[],
	);

	// Dialog state
	const [selectedTx, setSelectedTx] = useState<AdminTransaction | null>(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [isSavingFeedback, setIsSavingFeedback] = useState(false);

	// API for saving feedback and restoring deleted entries
	const { makeApiRequest } = useApi();
	const { restoreAndExport } = useRestoreAndExport();
	const [isRestoring, setIsRestoring] = useState(false);

	// Data fetching - only show professionals with tracked time
	const { professionals, loading: profLoading } = useProfessionals({
		activeOnly: true,
	});
	const {
		transactions,
		setTransactions,
		loading: txLoading,
		refetch: refetchTransactions,
	} = useUserTransactions(
		selectedDateStr, // Already in YYYY-MM-DD format
		selectedProfId,
		{ includeDeleted: true }, // Include deleted transactions for admin view
	);

	// Calendar tab data
	const { transactions: calendarTransactions, loading: calTxLoading } =
		useCalendarTransactions(calendarMonthDate, selectedProfId);

	// Generate unique day key for aggregation
	const dayKey = (d: Date) =>
		`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

	// Compute daily sums from calendar transactions
	const dailySums = useMemo(() => {
		const sums: Record<string, { unexported: number; exported: number }> = {};
		for (const tx of calendarTransactions) {
			const datePart = tx.date.split("T")[0];
			const [year, month, day] = datePart.split("-").map(Number);
			const d = new Date(year, month - 1, day);
			const k = dayKey(d);
			sums[k] = sums[k] || { unexported: 0, exported: 0 };
			if (tx.exported) {
				sums[k].exported += tx.units;
			} else {
				sums[k].unexported += tx.units;
			}
		}
		return sums;
	}, [calendarTransactions]);

	// Build 6x7 grid of days for the calendar month
	const calendarDays = useMemo(() => {
		const firstOfMonth = new Date(
			calendarMonthDate.getFullYear(),
			calendarMonthDate.getMonth(),
			1,
		);
		const startDay = new Date(firstOfMonth);
		const startWeekday = startDay.getDay();
		startDay.setDate(startDay.getDate() - startWeekday);
		const days: Date[] = [];
		for (let i = 0; i < 42; i++) {
			const d = new Date(startDay);
			d.setDate(startDay.getDate() + i);
			days.push(d);
		}
		return days;
	}, [calendarMonthDate]);

	const isSameDay = (a: Date, b: Date) =>
		a.getFullYear() === b.getFullYear() &&
		a.getMonth() === b.getMonth() &&
		a.getDate() === b.getDate();

	const goToPreviousMonth = () => {
		const d = new Date(calendarMonthDate);
		d.setMonth(d.getMonth() - 1);
		setCalendarMonthDate(d);
	};

	const goToNextMonth = () => {
		const d = new Date(calendarMonthDate);
		d.setMonth(d.getMonth() + 1);
		setCalendarMonthDate(d);
	};

	const goToToday = () => {
		setCalendarMonthDate(new Date());
	};

	// Click a day in calendar tab → switch to entries tab with that date
	const handleCalendarDayClick = (day: Date) => {
		if (!selectedProfId) return;
		setSelectedDate(day);
		setActiveTab("entries");
	};

	// Transform transactions to the canonical v2 entry shape used by the shared Entries table.
	const entries = useMemo(() => transactions.map(toEntry), [transactions]);

	// Apply application filter
	const filteredEntries = useMemo(
		() =>
			filterEntries(
				entries as Array<Entry & { application?: string }>,
			) as Entry[],
		[entries, filterEntries],
	);

	const sortBy = sorting[0]?.id ?? "date";
	const grouping = useMemo(() => [sortBy], [sortBy]);
	const tableColumnOrder = useMemo(
		() => [
			"_select",
			...DEFAULT_COLUMN_ORDER,
			"client",
			"task",
			"activity",
			"matterName",
			"billable",
			"unitsSeconds",
		],
		[],
	);
	const columnFilters = useMemo<ColumnFiltersState>(() => [], []);

	const table = useReactTable({
		data: filteredEntries,
		columns,
		state: {
			sorting,
			grouping,
			expanded,
			columnVisibility,
			columnOrder: tableColumnOrder,
			rowSelection,
			columnFilters,
			columnSizing,
		},
		onSortingChange: handleSortingChange,
		onExpandedChange: setExpanded,
		onRowSelectionChange: setRowSelection,
		onColumnSizingChange: setColumnSizing,
		enableRowSelection: false,
		enableGrouping: true,
		autoResetExpanded: false,
		enableColumnResizing: true,
		groupedColumnMode: false,
		columnResizeMode: "onEnd",
		defaultColumn: { minSize: 40, maxSize: 800 },
		getRowId: (row) => row.id,
		getCoreRowModel: getCoreRowModel(),
		getGroupedRowModel: getGroupedRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		meta: {} satisfies EntriesViewMeta,
	});

	const orderedEntries = table
		.getRowModel()
		.rows.filter((row: Row<Entry>) => !row.getIsGrouped())
		.map((row: Row<Entry>) => row.original);

	// Filter professionals by search
	const filteredProfs = useMemo(() => {
		if (!attorneySearch) return professionals.slice(0, 10);
		const term = attorneySearch.toLowerCase();
		return professionals
			.filter(
				(p) =>
					p.full_name.toLowerCase().includes(term) ||
					p.email?.toLowerCase().includes(term),
			)
			.slice(0, 10);
	}, [professionals, attorneySearch]);

	const selectedProfName = professionals.find(
		(p) => p.prof_id === selectedProfId,
	)?.full_name;

	// Handle row click - find original AdminTransaction and open dialog
	const handleRowClick = (entry: Entry) => {
		const entryId = Number(entry.id);
		const tx = transactions.find((t) => t.id === entryId);
		if (tx) {
			setSelectedTx(tx);
			setDialogOpen(true);
		}
	};

	// Navigation: compute current index and bounds based on SORTED entries (not raw transactions)
	// This ensures navigation respects the current sort/filter applied by the user
	const currentIndex = useMemo(() => {
		if (!selectedTx || !orderedEntries.length) return -1;
		return orderedEntries.findIndex((e) => Number(e.id) === selectedTx.id);
	}, [selectedTx, orderedEntries]);

	const hasPrevious = currentIndex > 0;
	const hasNext =
		currentIndex < orderedEntries.length - 1 && currentIndex !== -1;

	// Handle navigation between entries - use sorted/filtered entries array for correct order
	const handleNavigate = useCallback(
		(direction: "previous" | "next") => {
			const newIndex =
				direction === "previous" ? currentIndex - 1 : currentIndex + 1;
			if (newIndex >= 0 && newIndex < orderedEntries.length) {
				const entryId = Number(orderedEntries[newIndex].id);
				const tx = transactions.find((t) => t.id === entryId);
				if (tx) {
					setSelectedTx(tx);
				}
			}
		},
		[currentIndex, orderedEntries, transactions],
	);

	// Handle saving feedback
	const handleSaveFeedback = useCallback(
		async (payload: FeedbackPayload) => {
			setIsSavingFeedback(true);
			try {
				const response = await makeApiRequest({
					endpoint: `/admin/transactions/${payload.entryId}/feedback`,
					method: "POST",
					body: {
						approved: payload.approved,
						needs_improvement: payload.needsImprovement,
						deducted: payload.deducted,
						attorney_feedback: payload.attorneyFeedback,
						hourglass_feedback: payload.hourglassFeedback,
					},
				});

				if (response.error) {
					// Entry no longer exists in transactions table (exported or deleted)
					if (response.error.toLowerCase().includes("not found")) {
						toast.error(
							"This entry has been exported or deleted and can no longer be updated",
							{ duration: 5000 },
						);
						throw new Error("Entry not found");
					}
					throw new Error(response.error);
				}

				// Update transactions state so navigation shows correct data
				setTransactions((prev) =>
					prev.map((t) =>
						t.id === payload.entryId
							? {
									...t,
									approved: payload.approved,
									needs_improvement: payload.needsImprovement,
									deducted: payload.deducted,
									attorney_feedback: payload.attorneyFeedback,
									hourglass_feedback: payload.hourglassFeedback,
								}
							: t,
					),
				);

				logger.info("Feedback saved", {
					entryId: payload.entryId,
					approved: payload.approved,
					deducted: payload.deducted,
				});
				toast.success("Feedback saved");
			} catch (error) {
				const errorMsg = "Failed to save feedback";
				logger.error(errorMsg, {
					error: error instanceof Error ? error.message : String(error),
				});
				toast.error(errorMsg, { duration: 5000 });
				throw error; // Re-throw so dialog knows save failed
			} finally {
				setIsSavingFeedback(false);
			}
		},
		[makeApiRequest, setTransactions],
	);

	// Handle restore & export for deleted entries
	// isRestoring is managed here (not in the hook) so it covers the full operation including refetch
	const handleRestoreAndExport = useCallback(
		async (id: number): Promise<boolean> => {
			setIsRestoring(true);
			try {
				const success = await restoreAndExport(id);
				if (success) {
					await refetchTransactions();
				}
				return success;
			} finally {
				setIsRestoring(false);
			}
		},
		[restoreAndExport, refetchTransactions],
	);

	// Empty state message (matches attorney view pattern)
	const emptyMessage = useMemo(() => {
		if (!selectedProfId) return "Select an attorney to view their entries.";
		if (filteredEntries.length > 0) return undefined;
		if (entries.length > 0) return "No entries match the current filter.";
		return "No entries for this date.";
	}, [selectedProfId, filteredEntries.length, entries.length]);

	return (
		<div
			className={cn(
				"flex flex-col gap-4 p-6 bg-background h-full",
				styles.adminViewWrapper,
			)}
		>
			{/* Top row: Attorney selector (left) + Tab pills (right) */}
			<div className="flex items-center justify-between">
				{/* Professional Dropdown */}
				<Popover open={attorneyOpen} onOpenChange={setAttorneyOpen}>
					<PopoverTrigger asChild>
						<Button variant="outline" className="w-[220px] justify-between">
							{selectedProfName || "Select attorney..."}
							<ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
						</Button>
					</PopoverTrigger>
					<PopoverContent className="w-[280px] p-0">
						<Command>
							<CommandInput
								placeholder="Search..."
								value={attorneySearch}
								onValueChange={setAttorneySearch}
							/>
							<CommandList>
								<CommandEmpty>
									{profLoading ? "Loading..." : "No attorneys found."}
								</CommandEmpty>
								<CommandGroup>
									{filteredProfs.map((prof) => (
										<CommandItem
											key={prof.prof_id}
											value={`${prof.full_name} ${prof.email ?? ""}`}
											onSelect={() => {
												setSelectedProfId(prof.prof_id);
												setAttorneyOpen(false);
												setAttorneySearch("");
											}}
										>
											<Check
												className={cn(
													"mr-2 h-4 w-4",
													selectedProfId === prof.prof_id
														? "opacity-100"
														: "opacity-0",
												)}
											/>
											<div className="flex flex-col">
												<span>{prof.full_name}</span>
												<span className="text-xs text-muted-foreground">
													{prof.email}
												</span>
											</div>
										</CommandItem>
									))}
								</CommandGroup>
							</CommandList>
						</Command>
					</PopoverContent>
				</Popover>

				{/* Tab pills */}
				<Tabs
					value={activeTab}
					onValueChange={(v) => setActiveTab(v as "entries" | "calendar")}
				>
					<TabsList>
						<TabsTrigger value="entries">Entries</TabsTrigger>
						<TabsTrigger value="calendar">Calendar</TabsTrigger>
					</TabsList>
				</Tabs>
			</div>

			{/* Tab content */}
			{activeTab === "entries" ? (
				<>
					{/* Date picker + Application filter row */}
					<div className="flex items-center gap-3">
						<FiltersPopover
							isVisible={isApplicationVisible}
							onToggleApplication={toggleApplication}
							onShowAllApps={showAllApplications}
							onHideAllApps={hideAllApplications}
						/>
						<DatePicker
							className="w-fit"
							date={selectedDate}
							onDateChange={setSelectedDate}
							selectionMode="single"
						/>
					</div>

					{/* Entries table */}
					<div className="border border-border rounded-lg overflow-hidden flex-1 flex flex-col">
						{txLoading ? (
							<div className="flex-1 flex items-center justify-center text-muted-foreground">
								Loading...
							</div>
						) : (
							<EntriesTable
								table={table}
								isLoading={txLoading}
								rowSelection={rowSelection}
								onRowClick={selectedProfId ? handleRowClick : undefined}
								exportedRowsInert={false}
								emptyMessage={emptyMessage}
							/>
						)}
					</div>

					{/* Detail Dialog */}
					<AdminEntryDetailDialog
						entry={selectedTx}
						open={dialogOpen}
						onOpenChange={setDialogOpen}
						onNavigate={handleNavigate}
						hasPrevious={hasPrevious}
						hasNext={hasNext}
						onSaveFeedback={handleSaveFeedback}
						isSaving={isSavingFeedback}
						onRestoreAndExport={handleRestoreAndExport}
						isRestoring={isRestoring}
					/>
				</>
			) : (
				/* Calendar tab content */
				<>
					<div className="border border-border rounded-lg overflow-hidden flex-1 flex flex-col">
						{!selectedProfId ? (
							<div className="flex-1 flex items-center justify-center text-muted-foreground">
								Select an attorney to view their calendar
							</div>
						) : calTxLoading ? (
							<div className="flex-1 flex items-center justify-center text-muted-foreground">
								Loading...
							</div>
						) : (
							<div className={calendarStyles.calendarView}>
								{/* Calendar Header */}
								<div className={calendarStyles.calendarHeader}>
									<span className={calendarStyles.calendarHeaderLabel}>
										{calendarMonthDate.toLocaleDateString(undefined, {
											month: "long",
											year: "numeric",
										})}
									</span>
									<div className={calendarStyles.calendarHeaderButtonWrapper}>
										<button
											className={calendarStyles.calendarPageButton}
											onClick={goToPreviousMonth}
										>
											<img
												src={
													darkMode ? navArrowLeftWhiteIcon : navArrowLeftIcon
												}
												alt="Previous"
											/>
										</button>
										<button
											className={`${calendarStyles.calendarPageButton} ${calendarStyles.calendarTodayButton}`}
											onClick={goToToday}
										>
											Today
										</button>
										<button
											className={calendarStyles.calendarPageButton}
											onClick={goToNextMonth}
										>
											<img
												src={
													darkMode ? navArrowRightWhiteIcon : navArrowRightIcon
												}
												alt="Next"
											/>
										</button>
									</div>
								</div>

								{/* Calendar Grid */}
								<div className={calendarStyles.calendarGrid}>
									{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
										(day) => (
											<div
												key={day}
												className={calendarStyles.calendarWeekdayHeader}
											>
												{day}
											</div>
										),
									)}

									{calendarDays.map((currDay, index) => {
										const isCurrentMonth =
											currDay.getMonth() === calendarMonthDate.getMonth();
										const isWeekend =
											currDay.getDay() === 0 || currDay.getDay() === 6;
										const key = dayKey(currDay);
										const unexported = (
											dailySums[key]?.unexported || 0
										).toFixed(1);
										const exported = (dailySums[key]?.exported || 0).toFixed(1);

										return (
											<button
												key={index}
												onClick={() => handleCalendarDayClick(currDay)}
											>
												<div
													className={`${calendarStyles.calendarCard} ${
														!isCurrentMonth
															? calendarStyles.calendarOutsideMonthCard
															: ""
													} ${isWeekend ? calendarStyles.calendarWeekendCard : ""}`}
													data-today={isSameDay(currDay, new Date())}
												>
													<div className={calendarStyles.cardDate}>
														{currDay.getDate()}
													</div>
													<div
														className={`${calendarStyles.cardMetric} ${calendarStyles.cardMetricUnits}`}
													>
														{unexported} hrs to export
													</div>
													<div
														className={`${calendarStyles.cardMetric} ${calendarStyles.cardMetricUnits}`}
													>
														{exported} hrs exported
													</div>
												</div>
											</button>
										);
									})}
								</div>
							</div>
						)}
					</div>
				</>
			)}
		</div>
	);
}
